/**
 * "Execução dos serviços": acompanha o andamento item a item — número, item, responsável,
 * situação (Concluído / Em execução / Pendente), observação e ação. Lê o mesmo checklist da
 * O.S.; item técnico leva à execução técnica pelo botão de ações.
 */
import type { MaintenanceWorkOrderChecklistItemDto, MaintenanceWorkOrderDetailDto } from "@bi/types";
import { MaintenanceIcon } from "./MaintenanceIcons";
import { reasonText } from "./work-order-checklist-ui";

type Situacao = { rotulo: string; classe: string; ponto: string };

function situacao(i: MaintenanceWorkOrderChecklistItemDto, comRegistro: boolean): Situacao {
  if (i.status === "done") return { rotulo: "Concluído", classe: "bg-tm-50 text-tm-700", ponto: "bg-tm-500" };
  if (i.status === "not_done") return { rotulo: "Não realizado", classe: "bg-danger-50 text-danger-600", ponto: "bg-danger-600" };
  if (i.status === "not_applicable") return { rotulo: "Não se aplica", classe: "bg-gray-100 text-gray-600", ponto: "bg-gray-400" };
  if (comRegistro) return { rotulo: "Em execução", classe: "bg-info-50 text-info-700", ponto: "bg-info-500" };
  return { rotulo: "Pendente", classe: "bg-gray-100 text-gray-600", ponto: "bg-gray-400" };
}

export function WorkOrderServicesTable({
  o, onAbrirTecnico,
}: { o: MaintenanceWorkOrderDetailDto; onAbrirTecnico: (id: string) => void }) {
  const itens = o.checklist.items;
  if (!itens.length) return null;
  return (
    <section className="card overflow-hidden">
      <div className="flex items-start gap-3 p-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-tm-50 text-tm-700"><MaintenanceIcon name="generic" className="h-6 w-6" /></span>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Execução dos serviços</h3>
          <p className="text-sm text-gray-500">Acompanhe o andamento da execução dos itens da manutenção.</p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="border-y border-surface-border bg-surface-muted text-xs text-gray-500">
            <tr>
              <th className="w-10 px-4 py-2 text-left">#</th><th className="px-2 py-2 text-left">Item</th><th className="px-2 py-2 text-left">Responsável</th>
              <th className="px-2 py-2 text-left">Status</th><th className="px-2 py-2 text-left">Observação</th><th className="w-14 px-4 py-2 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {itens.map((i, n) => {
              const t = o.technical?.items[i.id];
              const sit = situacao(i, !!t && t.inspected > 0);
              const tecnico = i.executionType !== "simple_check";
              const obs = i.notes
                ?? (i.reason ? reasonText(i.reason) : null)
                ?? (tecnico && t && t.inspected > 0 ? `${t.inspected} registro(s)${t.attention + t.critical > 0 ? ` · ${t.attention + t.critical} com problema` : ""}` : null);
              return (
                <tr key={i.id}>
                  <td className="px-4 py-2 text-gray-500">{n + 1}</td>
                  <td className="px-2 py-2 font-medium text-gray-900">{i.itemName}</td>
                  <td className="px-2 py-2 text-gray-700">{i.completedByName ?? "—"}</td>
                  <td className="px-2 py-2">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${sit.classe}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${sit.ponto}`} />{sit.rotulo}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-gray-600">{obs ?? "—"}</td>
                  <td className="px-4 py-2 text-right">
                    {tecnico
                      ? <button type="button" className="rounded px-2 text-lg leading-none text-gray-500 hover:bg-surface-muted" aria-label={`Abrir execução de ${i.itemName}`} onClick={() => onAbrirTecnico(i.id)}>⋯</button>
                      : <span className="text-gray-300">⋯</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
